import { AlertTriangle } from "lucide-react";

import { useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import { useTenantVersionsQuery } from "@/api/tenantRuntime";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { sortCandidatesNewestFirst } from "@/lib/labModel";
import {
  compareDefaults,
  defaultChip,
  defaultSentence,
} from "@/lib/tenantVersions";
import { Badge } from "@/ui/Badge";
import { Notice } from "@/ui/Notice";

/**
 * The default a test version was built on beside the default its preview tenant
 * serves, warning when they differ. It never blocks anything. No `candidateId`
 * means the newest test version.
 */
export function DefaultNote(props: DefaultNoteProps) {
  // Without tenant tools there is no versions read, so nothing is requested.
  return useRuntimeConfig().features.tenantTools ? (
    <DefaultNoteLoaded {...props} />
  ) : null;
}

type DefaultNoteProps = {
  projectId: string;
  candidateId?: string | null;
  /** A badge for the Lab's top bar instead of a line. */
  compact?: boolean;
};

function DefaultNoteLoaded({
  projectId,
  candidateId,
  compact = false,
}: DefaultNoteProps) {
  const tenant = useProjectOverviewQuery(projectId).data?.project.previewTenantId;
  const candidates = usePreviewCandidatesQuery(projectId).data?.items ?? [];
  const versions = useTenantVersionsQuery(tenant, true).data;
  const candidate =
    candidates.find((item) => item.candidateId === candidateId) ??
    sortCandidatesNewestFirst(candidates)[0];
  const comparison = compareDefaults(
    versions,
    tenant,
    candidate?.basePackRevision,
  );
  if (comparison === null) return null;

  if (compact) {
    return comparison.state === "differs" ? (
      <Badge tone="warning" dir="ltr" className="gap-1 font-semibold">
        <AlertTriangle aria-hidden="true" className="h-3 w-3" />
        {defaultChip(comparison)}
      </Badge>
    ) : (
      <Badge tone="outline" dir="ltr">
        {defaultChip(comparison)}
      </Badge>
    );
  }
  return comparison.state === "differs" ? (
    <Notice
      icon={<AlertTriangle className="h-4 w-4 text-warning-foreground" />}
      className="border-warning-foreground/50 bg-warning font-medium text-warning-foreground"
    >
      {defaultSentence(comparison)}
    </Notice>
  ) : (
    <p className="text-sm text-muted-foreground">
      {defaultSentence(comparison)}
    </p>
  );
}
